import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import {defer, Observable} from 'rxjs';
import {finalize, map, tap} from 'rxjs/operators';
import {Invoice} from './invoice';

import {InvoiceReminder} from './invoice-reminder';
import {Payment} from '@app/payment/shared/payment';
import {InvoiceHistory} from './invoice-history';
import { ToastrService } from 'ngx-toastr';
import {ApiUtils} from '@shared/utils/api.utils';
import {BaseNumberApi} from '@shared/base/base-number-api';
import {File as UfFile} from '@shared/components/files/file';

@Injectable({
  providedIn: 'root'
})
export class InvoiceApi extends BaseNumberApi<Invoice> {
  readonly httpClient: HttpClient;
  private readonly toastrService = inject(ToastrService);

  constructor() {
    const httpClient = inject(HttpClient);

    super(httpClient, 'invoices');
  
    this.httpClient = httpClient;
  }

  create(): Invoice {
    return new Invoice();
  }

  public credit(id: number): Observable<Invoice> {
    return this.httpClient.get<Invoice>(`${this.endpoint}/${id}/credit`).pipe(map(value => {
      return Object.assign(new Invoice(), value);
    }));
  }

  public unpaid(): Observable<Invoice[]> {
    return this.httpClient.get<Invoice[]>(`${this.endpoint}/unpaid`);
  }

  public unpaidReport() {
    const progressToast = this.toastrService.info('Bezig met ophalen rapport', '', {
      'timeOut': 0,
      'extendedTimeOut': 0
    });

    this.httpClient.get(`${this.endpoint}/unpaid-report`, {
      responseType: 'blob',
      observe: 'response',
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).pipe(
      // The progress toast has no timeout, so clearing it must also happen when the request fails.
      finalize(() => this.toastrService.clear(progressToast.toastId))
    ).subscribe({
      next: response => {
        ApiUtils.saveToFileSystem(response);
        this.toastrService.success('Rapport opgehaald', null);
      },
      // The error toast itself is the responsibility of HttpErrorInterceptor.
      error: response => console.error(response)
    });
  }

  public history(number: number): Observable<InvoiceHistory[]> {
    return this.httpClient.get<InvoiceHistory[]>(`${this.endpoint}/${number}/history`).pipe(map(list => {
        return list.map(value => Object.assign(new InvoiceHistory(), value));
      })
    );
  }

  public reminders(number: number): Observable<InvoiceReminder[]> {
    return this.httpClient.get<InvoiceReminder[]>(`${this.endpoint}/${number}/reminders`);
  }

  public payments(number: number): Observable<Payment[]> {
    return this.httpClient.get<Payment[]>(`${this.endpoint}/${number}/payments`);
  }

  public payment(number: number, payment: Payment): Observable<Invoice> {
    return this.httpClient.post<Invoice>(`${this.endpoint}/${number}/payment`, payment).pipe(map(value => {
      return Object.assign(new Invoice(), value);
    }));
  }

  public download(id: number[], format: string) {
    const progressToast = this.toastrService.info('Bezig met ophalen factuur', '', {
      'timeOut': 0,
      'extendedTimeOut': 0
    });

    const ids = id.map(value => '' + value);

    this.httpClient.get(`${this.endpoint}/download`, {
      responseType: 'blob',
      observe: 'response',
      params: {
        id: ids,
        format: format
      },
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    }).pipe(
      // The progress toast has no timeout, so clearing it must also happen when the request fails.
      finalize(() => this.toastrService.clear(progressToast.toastId))
    ).subscribe({
      next: response => {
        ApiUtils.saveToFileSystem(response);
        this.toastrService.success('Factuur opgehaald', null);
      },
      // The error toast itself is the responsibility of HttpErrorInterceptor.
      error: response => console.error(response)
    });
  }

  /**
   * The progress toast is created inside `defer` so it only appears once a caller actually
   * subscribes; created eagerly it would be shown for a request that is never sent, and because
   * it has no timeout it would then stay on screen forever. `finalize` clears it on every exit —
   * success, error and early unsubscribe alike. The error toast is left to HttpErrorInterceptor.
   */
  public email(id: number[]): Observable<HttpResponse<Blob>> {
    const ids = id.map(value => '' + value);

    return defer(() => {
      const progressToast = this.toastrService.info('Bezig met het verzenden van de emails', '', {
        'timeOut': 0,
        'extendedTimeOut': 0
      });

      return this.httpClient.get(`${this.endpoint}/mail`, {
        responseType: 'blob',
        observe: 'response',
        params: {
          id: ids
        }
      }).pipe(
        tap(() => this.toastrService.success('Email staat in de wachtrij voor verzending', null)),
        finalize(() => this.toastrService.clear(progressToast.toastId))
      );
    });
  }

  public last10(): Observable<Invoice[]> {
    return this.httpClient.get<Invoice[]>(`${this.endpoint}/last10`);
  }

  send2bookkeeping(selected: number[]) {
    selected.forEach(value => {
      this.httpClient.get(`${this.endpoint}/${value}/send-to-bookkeeping`).subscribe(() => {
        this.toastrService.success('Verzonden naar de boekhouding', null);
      });
    });
  }

  public getFiles(uuid: string): Observable<UfFile[]> {
    return this.httpClient.get<UfFile[]>(`${this.endpoint}/${uuid}/files`);
  }

  public uploadFiles(uuid: string, file: File): Observable<UfFile> {
    const formData = new FormData();
    formData.append("file", file);

    return this.httpClient.post<UfFile>(`${this.endpoint}/${uuid}/files`, formData);
  }

  public deleteFile(uuid: string, fileUuid: string): Observable<UfFile> {
    return this.httpClient.delete<UfFile>(`${this.endpoint}/${uuid}/files/${fileUuid}`);
  }
}
